import { Form, Input, Button, DatePicker, Select, Radio, Upload } from "antd";
import CategorizedUpload from "../../components/comps/uploads";

import {
  UploadOutlined,
  LoadingOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
// import store from '../../store/values-store';
import Settings from "../../dependencies/custom/settings";
import CustomFunctions from "../../dependencies/custom/custom-functions";
const { Option } = Select;

function rulesWithValidator(msg = "Field is required") {
  return [
    {
      required: true,
      async validator(object, value) {
        if (!value) {
          return Promise.reject(new Error(msg));
        }
      },
    },
  ];
}

function simpleRules(msg = "Field is required") {
  return { required: true, message: msg };
}

export const StudentInfo = (form, valuesStore) => {
  const klasses = Array.isArray(valuesStore?.getValue("klasses"))
    ? valuesStore
        .getValue("klasses")
        .map((item) => ({ value: item.id, label: item.name }))
    : [];

  const nationality = Array.isArray(valuesStore?.getValue("nationality"))
    ? valuesStore
        .getValue("nationality")
        .map((item) => ({ value: item.nationality, label: item.nationality }))
    : [];

  return {
    title: "Student Information",
    name: "student",
    endpoint: Settings.urls.enrollment.student,
    fields: [
      "firstName",
      "lastName",
      "middleName",
      "dob",
      "gender",
      "nationality",
      "previousSchool",
      "classApplied",
      "dayBoarding",
    ],
    content: (
      <>
        <Form.Item name="firstName" label="First Name" rules={[simpleRules()]}>
          <Input />
        </Form.Item>
        <Form.Item name="lastName" label="Last Name" rules={[simpleRules()]}>
          <Input />
        </Form.Item>

        <Form.Item name="middleName" label="Middle Name">
          <Input />
        </Form.Item>

        <Form.Item name="dob" label="Date of Birth" rules={[simpleRules()]}>
          <DatePicker
            onChange={(date) => {
              if (dayjs.isDayjs(date) && date.isValid()) {
                const age = dayjs().diff(date, "year");
                form.setFieldValue("age", age);
              }
            }}
          />
        </Form.Item>

        <Form.Item label="Age" name="age">
          <Input disabled />
        </Form.Item>

        <Form.Item name="gender" label="Gender" rules={[simpleRules()]}>
          <Select>
            <Option value="Male">Male</Option>
            <Option value="Female">Female</Option>
          </Select>
        </Form.Item>

        <Form.Item
          name="nationality"
          label="Nationality"
          rules={[simpleRules()]}
        >
          <Select showSearch options={nationality} />
        </Form.Item>

        <Form.Item
          name="previousSchool"
          label="Previous School"
          extra="Not required for Baby Class"
          dependencies={["classApplied"]}
          rules={[
            ({ getFieldValue }) => ({
              validator(_, value) {
                const classApplied = getFieldValue("classApplied");
                if (classApplied && classApplied != 2 && !value) {
                  return Promise.reject(
                    new Error("Previous School is required"),
                  );
                }
                return Promise.resolve();
              },
            }),
          ]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          name="linField"
          label="Learner Identification Number (LIN)"
          rules={[{ max: 30, message: "LIN must be 30 characters or fewer" }]}
        >
          <Input
            disabled
            maxLength={30}
            placeholder="To be filled in by the school"
          />
        </Form.Item>

        <Form.Item
          name="classApplied"
          label="Class Applied For"
          rules={[simpleRules()]}
        >
          <Select
            showSearch
            options={klasses}
            onChange={() => form.setFieldValue("dayBoarding", undefined)}
          />
        </Form.Item>

        <Form.Item
          noStyle
          shouldUpdate={(prevValues, currentValues) =>
            prevValues.classApplied !== currentValues.classApplied
          }
        >
          {({ getFieldValue }) => {
            const classApplied = getFieldValue("classApplied");
            if (!classApplied) return null;

            // Read fresh from store (model_to_dict uses snake_case field names)
            const rawKlasses = Array.isArray(valuesStore?.getValue("klasses"))
              ? valuesStore.getValue("klasses")
              : [];
            // eslint-disable-next-line eqeqeq
            const selectedClass = rawKlasses.find((k) => k.id == classApplied);
            const classGroup = selectedClass?.class_group;
            const className = selectedClass?.name || "";

            // P6 & P7 are in 'Upper Primary' along with P4/P5, so identify them by name
            const BOARDING_ONLY = ["Primary Six (P.6)", "Primary Seven (P.7)"];

            if (classGroup === "PreSchool") {
              return (
                <Form.Item
                  name="dayBoarding"
                  label="Day/Boarding"
                  initialValue="Day"
                  rules={[simpleRules()]}
                >
                  <Radio.Group>
                    <Radio value="Day">Day</Radio>
                  </Radio.Group>
                </Form.Item>
              );
            }

            if (BOARDING_ONLY.includes(className)) {
              return (
                <Form.Item
                  name="dayBoarding"
                  label="Day/Boarding"
                  initialValue="Boarding"
                  rules={[simpleRules()]}
                >
                  <Radio.Group>
                    <Radio value="Boarding">Boarding</Radio>
                  </Radio.Group>
                </Form.Item>
              );
            }

            if (
              classGroup === "Lower Primary" ||
              classGroup === "Upper Primary"
            ) {
              return (
                <Form.Item
                  name="dayBoarding"
                  label="Day/Boarding"
                  rules={[simpleRules()]}
                >
                  <Radio.Group>
                    <Radio value="Day">Day</Radio>
                    <Radio value="Boarding">Boarding</Radio>
                  </Radio.Group>
                </Form.Item>
              );
            }

            return null;
          }}
        </Form.Item>
      </>
    ),
  };
};

export const ParentGaurdian = (form, valuesStore) => {
  // console.log(Settings.urls.enrollment.guardian)
  // const relationship = valuesStore?.getValue('relationship')?.map((item) => ({ value: item.id, label: item.name }));
  const relationship = Array.isArray(valuesStore?.getValue("relationship"))
    ? valuesStore
        .getValue("relationship")
        .map((item) => ({ value: item.id, label: item.name }))
    : [];
  return {
    title: "Parent/Guardian",
    name: "gaurdian",
    endpoint: Settings.urls.enrollment.guardian,
    fields: [
      "parentName",
      "nationalIDChoice",
      "parentNationalIDNum",
      "relationshipId",
      "phonePrimary",
      "phoneAlt",
      "email",
      "address",
      "occupation",
      "countryCodePri",
      "countryCodeAlt",
    ],
    content: (
      <>
        <Form.Item
          name="parentName"
          label="Parent/Guardian Full Name"
          rules={[simpleRules()]}
        >
          <Input placeholder="Parent/Guardian Full Name" />
        </Form.Item>

        <Form.Item
          name="nationalIDChoice"
          label="ID Type"
          rules={[simpleRules()]}
        >
          <Select
            options={[
              { label: "National ID", value: "National ID" },
              { label: "Passport", value: "Passport" },
            ]}
            placeholder="ID Type"
          />
        </Form.Item>

        <Form.Item
          name="parentNationalIDNum"
          label="Parent/Guardian ID Number"
          dependencies={["nationalIDChoice"]}
          rules={[
            ({ getFieldValue }) => ({
              validator(_, value) {
                const nationalIDChoice = getFieldValue("nationalIDChoice");

                if (
                  nationalIDChoice &&
                  nationalIDChoice == "National ID" &&
                  value.length != 14
                ) {
                  return Promise.reject(
                    new Error("National ID Number must be 14 characters"),
                  );
                }
                if (
                  nationalIDChoice &&
                  nationalIDChoice == "Passport" &&
                  value.length != 9
                ) {
                  return Promise.reject(
                    new Error("Passport must be 9 characters"),
                  );
                }
                return Promise.resolve();
              },
            }),
            { required: true },
          ]}
        >
          <Input placeholder="Parent/Guardian ID Number" />
        </Form.Item>

        <Form.Item
          name="relationshipId"
          label="Relationship to Student"
          rules={[simpleRules()]}
        >
          <Select
            showSearch
            options={relationship}
            placeholder="Relationship"
          />
        </Form.Item>

        <Form.Item
          name="phonePrimary"
          label="Phone Number (Primary)"
          rules={[simpleRules()]}
        >
          <Input
            type="number"
            addonBefore={CustomFunctions.prefixSelector("countryCodePri")}
            style={{ width: "100%" }}
          />
        </Form.Item>

        <Form.Item name="phoneAlt" label="Alternative Phone">
          <Input
            type="number"
            addonBefore={CustomFunctions.prefixSelector("countryCodeAlt")}
            style={{ width: "100%" }}
          />
        </Form.Item>

        <Form.Item
          name="email"
          label="Email Address"
          rules={[{ ...simpleRules(), type: "email" }]}
        >
          <Input placeholder="Email" />
        </Form.Item>

        <Form.Item
          name="address"
          label="Residential Address"
          rules={[simpleRules()]}
        >
          <Input.TextArea placeholder="Residential Address" rows={1} />
        </Form.Item>

        <Form.Item name="occupation" label="Occupation/Employer">
          <Input placeholder="Occupation" />
        </Form.Item>
      </>
    ),
  };
};

export const EmergencyContact = (form, valuesStore) => {
  // const relationship = valuesStore?.getValue('relationship')?.map((item) => ({ value: item.id, label: item.name }));
  const relationship = Array.isArray(valuesStore?.getValue("relationship"))
    ? valuesStore
        .getValue("relationship")
        .map((item) => ({ value: item.id, label: item.name }))
    : [];
  return {
    title: "Emergency Contact",
    name: "emergency",
    endpoint: Settings.urls.enrollment.emergency,
    fields: ["emgName", "emgRelId", "emgPhone", "countryCodeEmg"],
    content: (
      <>
        <Form.Item name="emgName" label="Name" rules={[simpleRules()]}>
          <Input placeholder="Name" />
        </Form.Item>

        <Form.Item name="emgRelId" label="Relationship" rules={[simpleRules()]}>
          <Select
            showSearch
            placeholder="Relationship"
            options={relationship}
          />
        </Form.Item>

        <Form.Item name="emgPhone" label="Phone Number" rules={[simpleRules()]}>
          <Input
            placeholder="Phone Number"
            type="number"
            addonBefore={CustomFunctions.prefixSelector("countryCodeEmg")}
          />
        </Form.Item>
      </>
    ),
  };
};

export const Uploads = (
  form,
  data = [],
  fileList,
  setFileList,
  removeFile,
  categoryRules,
  locked,
) => {
  return {
    title: "Upload",
    name: "uploads",
    endpoint: Settings.urls.enrollment.uploads,
    fields: ["birthCert", "reportCard", "passportPhoto", "parentIDCard"],
    content: (
      <>
        <CategorizedUpload
          locked={locked}
          data={data}
          fileList={fileList}
          setFileList={setFileList}
          removeFile={removeFile}
          form={form}
          categoryRules={categoryRules}
        />
      </>
    ),
  };
};

// export const Uploads = (form, imageUrl) => {
//     return {
//         title: "Uploads",
//         name: 'uploads',
//         endpoint: Settings.urls.enrollment.uploads,
//         fields: ['birthCert', 'transferLetter', 'passportPhoto', 'parentIDCard'],
//         content: <> <Form.Item
//             name="birthCert"
//             label="Birth Certificate"
//             valuePropName="fileList"
//             getValueFromEvent={(e) => e.fileList}
//             rules={[simpleRules()]}
//         >
//             {/* <Upload beforeUpload={() => false} accept=".pdf,.jpg,.jpeg,.png">
//                 <Button icon={<UploadOutlined />}>Upload Birth Certificate</Button>
//             </Upload> */}
//             <Upload.Dragger beforeUpload={() => false} accept=".pdf,.jpg,.jpeg,.png" maxCount={1}>
//                 <p className="ant-upload-drag-icon">
//                     <UploadOutlined />
//                 </p>
//                 <p className="ant-upload-text">Click or drag Birth Certificate to this area</p>
//             </Upload.Dragger>
//         </Form.Item>

//             <Form.Item
//                 name="transferLetter"
//                 label="Transfer Letter"
//                 valuePropName="fileList"
//                 getValueFromEvent={(e) => e.fileList}
//             >
//                 {/* <Upload beforeUpload={() => false} accept=".pdf,.jpg,.jpeg">
//                     <Button icon={<UploadOutlined />}>Upload Transfer Letter</Button>
//                 </Upload> */}
//                 <Upload.Dragger beforeUpload={() => false} accept=".pdf,.jpg,.jpeg,.png" maxCount={1}>
//                     <p className="ant-upload-drag-icon">
//                         <UploadOutlined />
//                     </p>
//                     <p className="ant-upload-text">Click or drag Transfer Letter to this area</p>
//                 </Upload.Dragger>
//             </Form.Item>

//             <Form.Item
//                 name="passportPhoto"
//                 label="Passport Photo"
//                 valuePropName="fileList"
//                 getValueFromEvent={(e) => e.fileList}
//                 rules={[simpleRules()]}
//             >
//                 {/* <Upload beforeUpload={() => false} accept=".jpg,.jpeg,.png">
//                     <Button icon={<UploadOutlined />}>Upload Passport Photo</Button>
//                 </Upload> */}
//                 <Upload.Dragger beforeUpload={() => false} accept=".jpg,.jpeg,.png" maxCount={1}>
//                     <p className="ant-upload-drag-icon">
//                         <UploadOutlined />
//                     </p>
//                     <p className="ant-upload-text">Click or drag Passport Photo to this area</p>
//                 </Upload.Dragger>
//             </Form.Item>

//             <Form.Item
//                 name="parentIDCard"
//                 label="Parent/Guardian National ID"
//                 valuePropName="fileList"
//                 getValueFromEvent={(e) => e.fileList}
//                 rules={[simpleRules()]}
//             >
//                 {/* <Upload beforeUpload={() => false} accept=".pdf,.jpg,.jpeg">
//                     <Button icon={<UploadOutlined />}>Upload ID</Button>
//                 </Upload> */}

//                 <Upload.Dragger beforeUpload={() => false} accept=".pdf,.jpg,.jpeg" maxCount={1}>
//                     <p className="ant-upload-drag-icon">
//                         <UploadOutlined />
//                     </p>
//                     <p className="ant-upload-text">Click or drag Parent or Guardian National ID to this area</p>
//                 </Upload.Dragger>

//             </Form.Item>
//         </>
//     }
// }
